import React, { useCallback, useEffect, useState } from 'react';
import { HistoryIcon } from 'lucide-react';
import { loadWorkflowHistory, type WorkflowHistoryItem } from '../../lib/workflow';
type WorkflowHistoryPanelProps = {
  prospectId: string;
  refreshKey?: number;
};
export function WorkflowHistoryPanel({
  prospectId,
  refreshKey = 0
}: WorkflowHistoryPanelProps) {
  const [items, setItems] = useState<WorkflowHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setItems(await loadWorkflowHistory(prospectId));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar el historial del proceso.');
    } finally {
      setIsLoading(false);
    }
  }, [prospectId]);
  useEffect(() => {
    void load();
  }, [load, refreshKey]);
  return <section id="workflow-panel" role="tabpanel" aria-labelledby="workflow" aria-busy={isLoading} data-id="element-659">
      <div className="flex items-center gap-3" data-id="element-660">
        <div className="rounded-xl bg-cyan-300/10 p-2.5 text-cyan-200" data-id="element-661">
          <HistoryIcon className="h-5 w-5" data-id="element-662" />
        </div>
        <div data-id="element-663">
          <h2 className="text-lg font-bold text-white" data-id="element-664">
            Historial del proceso
          </h2>
          <p className="text-sm text-slate-400" data-id="element-665">
            Cambios registrados por el workflow.
          </p>
        </div>
      </div>

      {isLoading && <p className="mt-5 text-sm text-slate-400" data-id="element-666">Cargando historial…</p>}
      {error && <p role="alert" className="mt-5 rounded-xl border border-rose-400/20 bg-rose-400/10 p-3 text-sm text-rose-100" data-id="element-667">
          {error}
        </p>}
      {!isLoading && !error && items.length === 0 && <p className="mt-5 rounded-xl border border-dashed border-white/15 p-4 text-sm text-slate-400" data-id="element-668">
          Todavía no hay cambios registrados en el workflow.
        </p>}

      {!isLoading && !error && items.length > 0 && <ol className="mt-6 space-y-0" data-id="element-669">
          {items.map((item, index) => <li key={item.id} className="relative grid grid-cols-[1.5rem_minmax(0,1fr)] gap-3 pb-6 last:pb-0" data-id="element-670">
              {index < items.length - 1 && <span aria-hidden="true" className="absolute left-3 top-6 h-[calc(100%-0.75rem)] w-px bg-white/10" data-id="element-671" />}
              <span aria-hidden="true" className="mt-1 flex h-6 w-6 rounded-full border-2 border-[#0b1728] bg-cyan-300 ring-1 ring-cyan-300/30" data-id="element-672" />
              <div className="min-w-0 rounded-xl bg-white/[0.03] p-3" data-id="element-673">
                <p className="break-words text-sm font-semibold text-slate-100" data-id="element-674">
                  {item.previousStep || 'Inicio'}{' '}
                  <span className="mx-1 text-slate-500" data-id="element-675">→</span>{' '}
                  {item.nextStep || 'Sin paso indicado'}
                </p>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400" data-id="element-676">
                  <span data-id="element-677">{item.changeType || 'Cambio de workflow'}</span>
                  {item.userName && <span data-id="element-678">por {item.userName}</span>}
                  {item.occurredAt && <time dateTime={item.occurredAt} data-id="element-679">
                      {formatDate(item.occurredAt)}
                    </time>}
                </div>
                {item.reason && <p className="mt-2 break-words text-sm text-slate-300" data-id="element-680">
                    {item.reason}
                  </p>}
              </div>
            </li>)}
        </ol>}
    </section>;
}
function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Fecha no disponible';
  return new Intl.DateTimeFormat('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
}